/**
 * Date formatting utilities for the admin interface.
 *
 * Per admin-requirements.md §1.4: Dates render MM-DD-YYYY.
 * Month names are included in keyword search.
 */

type CalendarDateOnly = { year: number; month: number; day: number };

/**
 * Recognise a **calendar-date-only** string: exactly `YYYY-MM-DD`,
 * `YYYY-MM-DDT00:00:00Z`, or `YYYY-MM-DDT00:00:00.000Z` (UTC midnight, no
 * other time-of-day). These come from date-only DB columns (campaign
 * start/end, report day buckets) serialized via `Date#toISOString()` or a
 * `::date::text` cast — there is no real time-of-day to respect, so the
 * calendar day must render the same in every browser time zone instead of
 * shifting with `new Date(iso)` + local getters (a US viewer west of UTC
 * would otherwise see the previous day).
 *
 * Returns the parsed digits for a real calendar date, the literal
 * `'invalid'` when the string has the right shape but names an impossible
 * date (e.g. `'2026-02-30'`, `'2026-13-01'` — `new Date` would silently
 * roll these over to a different date, which is worse than showing
 * nothing), or `null` when the string isn't shaped like a calendar-only
 * date at all (a real timestamp or non-date string — callers fall back to
 * the existing local-time `new Date(iso)` path for these).
 *
 * The one edge this creates: a genuine timestamp that happens to land on
 * exactly `00:00:00.000Z` is indistinguishable from a date-only value and
 * renders as its **UTC** date rather than its local date.
 */
function parseCalendarDateOnly(iso: string): CalendarDateOnly | 'invalid' | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/.exec(iso);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (
    utc.getUTCFullYear() !== year ||
    utc.getUTCMonth() !== month - 1 ||
    utc.getUTCDate() !== day
  ) {
    return 'invalid';
  }
  return { year, month, day };
}

/**
 * Format an ISO date string or Date to MM-DD-YYYY.
 *
 * A calendar-date-only string (see `parseCalendarDateOnly`) is rendered
 * from its digits directly, with no `Date` and no time zone involved, so
 * it shows the same calendar day everywhere. Every other input — a real
 * timestamp string, a `Date` object, or garbage — keeps the pre-existing
 * `new Date(iso)` + local-getters behavior (garbage still renders '').
 */
export function fmtDate(iso: string | Date | undefined | null): string {
  if (!iso) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  if (typeof iso === 'string') {
    const calendarDate = parseCalendarDateOnly(iso);
    if (calendarDate === 'invalid') return '';
    if (calendarDate) {
      return `${pad(calendarDate.month)}-${pad(calendarDate.day)}-${calendarDate.year}`;
    }
  }
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${d.getFullYear()}`;
}

/**
 * Extract the month name from an ISO date string for search indexing.
 *
 * Same calendar-date-only rule as `fmtDate`: a date-only string reads its
 * month straight from the digits (no time zone); everything else keeps
 * the local-time `new Date(iso)` path. See `parseCalendarDateOnly` for the
 * exact shape recognised and the UTC-midnight-timestamp edge case.
 */
export function monthName(iso: string | Date | undefined | null): string {
  if (!iso) return '';
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  if (typeof iso === 'string') {
    const calendarDate = parseCalendarDateOnly(iso);
    if (calendarDate === 'invalid') return '';
    if (calendarDate) return months[calendarDate.month - 1];
  }
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  return months[d.getMonth()];
}

/**
 * Format a number with commas (e.g., 1,234).
 */
export function fmtCount(n: number | undefined | null): string {
  if (n == null) return '';
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * USD with exactly two decimals and group separators (epic 097 D6, QA-44):
 * `$9,471.51`, `$0.00`. A negative keeps the pre-separator convention — minus
 * inside the dollar prefix (`$-1,204.10`) — so every caller's render changes
 * only in gaining the commas.
 */
const usd = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Format money (USD) - e.g., $1.85.
 */
export function fmtMoney(n: number | string | undefined | null): string {
  if (n == null) return '';
  const num = typeof n === 'string' ? parseFloat(n) : n;
  return `$${usd.format(num)}`;
}

/**
 * Format datetime to ISO string for display.
 */
export function fmtDateTime(iso: string | Date | undefined | null): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
}
